import React from "react";
import { FlatButton, TextField } from "material-ui";
import moment from "moment";

import UserStore from "../../../stores/UserStore";
import BoxOfficeActions from "../../../actions/BoxOfficeActions";

import NumberSegmentedInput from "../number-segmented-input.jsx";
import LinkLike from "../link-like.jsx";
import BaseOverlay from "../overlays/base.jsx";
import InlineNoticeLabel from "../inline-notice-label.jsx";

const IS_SIMULATED =
  window.location.search.indexOf("TERMINAL_SIMULATED=1") !== -1;

const READER_SAVED_KEY = "eventive--savedTerminalReader";
const CONNECT_TIMEOUT = 10000;

const BASE_STYLE = {
  display: "inline-block",
  padding: "5px 10px",
  borderRadius: 5,
  background: "#fff",
  boxShadow: "0px 0px 7px 1px rgba(0,0,0,0.2)"
};

class Terminal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pairingCode: "",
      modalOpen: false,
      pairingTerminal: false,
      pairingError: "",
      lastDisplayData: null,
      pairedReader: null
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.pairTerminal = this.pairTerminal.bind(this);
    this.disconnectTerminal = this.disconnectTerminal.bind(this);
  }

  static contextTypes = {
    router: React.PropTypes.object
  };

  componentWillMount() {
    setTimeout(() => {
      this.props.initializeTerminal();
      let savedReaderId = window.localStorage.getItem(READER_SAVED_KEY);
      if (
        savedReaderId &&
        this.props.terminalConnectionStatus === "not_connected"
      ) {
        this.setState({ pairingCode: savedReaderId }, () => {
          this.pairTerminal();
        });
      }
    }, 0);
  }

  componentWillReceiveProps(props) {
    const {
      processedData,
      terminal,
      terminalConnectionStatus,
      orderInProgress
    } = props;
    if (
      processedData &&
      processedData.subitems &&
      terminal &&
      terminalConnectionStatus === "connected" &&
      !orderInProgress
    ) {
      const displayData = {
        type: "cart",
        cart: {
          line_items: processedData.subitems
            .filter(i => i.quantity)
            .map(item => {
              return {
                description: item.name,
                amount: item.price,
                quantity: Number.isInteger(item.quantity) ? item.quantity : 1
              };
            }),
          total: processedData.total,
          currency: (UserStore.getUser() || { currency: "usd" }).currency
        }
      };

      this.setState(prevState => {
        // only set the display if the data has changed
        if (
          JSON.stringify(displayData) !==
          JSON.stringify(prevState.lastDisplayData)
        ) {
          if (displayData.cart.line_items.length < 1) {
            terminal.clearReaderDisplay();
          } else {
            terminal.setReaderDisplay(displayData);
          }
        }

        return {
          ...prevState,
          lastDisplayData: displayData
        };
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    if (this.props.terminalConnectionStatus === "connected") {
      this.props.terminal.clearReaderDisplay();
    }
  }

  openModal() {
    this.setState({ modalOpen: true });
  }

  closeModal() {
    this.setState({
      modalOpen: false,
      forceOpen: false,
      pairingTerminal: false,
      pairingCode: ""
    });
  }

  async pairTerminal() {
    this.setState({ pairingTerminal: true, pairingError: "" });

    const config = { simulated: IS_SIMULATED };
    const discoverResult = await this.props.terminal.discoverReaders(config);
    if (discoverResult.error) {
      this.setState({
        pairingTerminal: false,
        pairingCode: "",
        pairingError: discoverResult.error
      });
    } else if (discoverResult.discoveredReaders.length === 0) {
      this.setState({
        pairingTerminal: false,
        pairingCode: "",
        pairingError:
          "No readers found. Make sure the reader is connected to the internet and try again."
      });
    } else {
      const reader = discoverResult.discoveredReaders.find(
        r =>
          IS_SIMULATED ||
          r.serial_number.replace(/\d{9}(?!\d)/g, "") ===
            this.state.pairingCode ||
          ((r.device_type === "bbpos_wisepos_e" ||
            r.device_type === "stripe_s700_devkit" ||
            r.device_type === "stripe_s700") &&
            r.serial_number.indexOf(this.state.pairingCode) !== -1)
      );
      if (!reader) {
        console.log(
          "[Eventive Terminal] Invalid code, readers found:",
          discoverResult.discoveredReaders
        );
        this.setState({
          pairingTerminal: false,
          pairingCode: "",
          pairingError:
            "Invalid pairing code or reader not found. Make sure the reader is connected to the internet, check the code, and try again."
        });
      } else if (reader.status === "offline") {
        this.setState({
          pairingTerminal: false,
          pairingCode: "",
          pairingError:
            "Reader offline. Make sure the reader is connected to the internet and try again."
        });
      } else {
        this.timeout = setTimeout(() => {
          this.setState({
            pairingTerminal: false,
            pairingCode: "",
            pairingError: "Unable to connect to reader."
          });
          BoxOfficeActions.terminalConnectionStatusChange("not_connected");
        }, CONNECT_TIMEOUT);
        const connectResult = await this.props.terminal.connectReader(reader);
        clearTimeout(this.timeout);
        if (connectResult.error) {
          BoxOfficeActions.terminalConnectionStatusChange("not_connected");
          this.setState({
            pairingTerminal: false,
            pairingCode: "",
            pairingError: connectResult.error
          });
        } else {
          try {
            window.localStorage.setItem(
              READER_SAVED_KEY,
              this.state.pairingCode
            );
          } catch (e) {
            /* fall through */
          }
          console.log(
            "[Eventive Terminal] Connected to reader:",
            connectResult.reader
          );
          this.setState(
            {
              pairingTerminal: false,
              pairedReader: connectResult.reader,
              pairingCode: "",
              pairingError: ""
            },
            () => {
              this.closeModal();
            }
          );
        }
      }
    }
  }

  disconnectTerminal() {
    this.props.terminal.disconnectReader();
    try {
      window.localStorage.removeItem(READER_SAVED_KEY);
    } catch (e) {
      /* fall through */
    }
    this.closeModal();
  }

  render() {
    const { terminalConnectionStatus, terminalPaymentStatus, terminal } =
        this.props,
      { forceOpen, pairingTerminal, pairingCode, pairingError, pairedReader } =
        this.state;

    const isConnected = terminalConnectionStatus === "connected";
    const isConnecting = terminalConnectionStatus === "connecting";

    const terminalInProgress =
      terminalPaymentStatus === "processing" ||
      terminalPaymentStatus === "waiting_for_input";

    return (
      <div style={Object.assign({}, BASE_STYLE)}>
        <BaseOverlay
          open={this.state.modalOpen || terminalInProgress}
          onRequestClose={!forceOpen && this.closeModal}
          useMuiDefaultStyle={true}
          dialogContentStyle={{ maxWidth: 400 }}
          actions={
            terminalInProgress
              ? []
              : [
                  <FlatButton
                    label="Cancel"
                    onTouchTap={
                      forceOpen ? this.context.router.goBack : this.closeModal
                    }
                  />,
                  <FlatButton
                    primary={true}
                    disabled={pairingTerminal}
                    label={
                      pairingTerminal || isConnecting
                        ? "Connecting..."
                        : isConnected
                        ? "Disconnect Terminal"
                        : "Connect Terminal"
                    }
                    onTouchTap={
                      isConnected ? this.disconnectTerminal : this.pairTerminal
                    }
                  />
                ]
          }
        >
          <h3 style={{ textAlign: "center" }}>
            Eventive Terminal{" "}
            <InlineNoticeLabel
              label="Beta"
              style={{ verticalAlign: 3, marginLeft: 3 }}
            />
          </h3>
          {terminalInProgress ? (
            <div style={{ textAlign: "center" }}>
              <p>
                {terminalPaymentStatus === "waiting_for_input"
                  ? "Waiting for customer..."
                  : "Processing payment..."}
              </p>
            </div>
          ) : isConnected ? (
            <div style={{ textAlign: "center" }}>
              <p>Currently connected to Eventive Terminal.</p>
            </div>
          ) : isConnecting ? (
            <div style={{ textAlign: "center" }}>
              <p>Connecting to reader...</p>
            </div>
          ) : (
            <div>
              <p>
                Use the dedicated countertop Eventive Terminal for fast & secure
                EMV, contactless, and swipe payments.{" "}
                <LinkLike
                  onTouchTap={() =>
                    Intercom(
                      "showNewMessage",
                      "I'm interested in Eventive Terminal rental / purchase options!"
                    )
                  }
                >
                  Available for rent or purchase.
                </LinkLike>
              </p>
              <p>
                Have an Eventive Terminal? Connect it to the internet, then
                enter its pairing code.
              </p>
              <div style={{ marginTop: 36, textAlign: "center" }}>
                <NumberSegmentedInput
                  digits={9}
                  chunkSize={3}
                  value={pairingCode}
                  onChange={pairingCode =>
                    this.setState({ pairingCode, pairingError: "" })
                  }
                  onFinish={this.pairTerminal}
                  disabled={isConnecting}
                  autoFocus
                />
              </div>
              {pairingError ? (
                <div
                  style={{ marginTop: 24, textAlign: "center", color: "red" }}
                >
                  {pairingError.message || pairingError}
                </div>
              ) : null}
            </div>
          )}
        </BaseOverlay>

        {isConnecting ? (
          <span style={{ color: "rgb(33,33,33)" }}>Terminal connecting...</span>
        ) : isConnected ? (
          <span>
            <strong>Terminal connected</strong> &middot;{" "}
            <LinkLike onTouchTap={this.openModal}>Manage</LinkLike>
          </span>
        ) : (
          <span>
            <LinkLike onTouchTap={this.openModal}>
              Connect Eventive Terminal
            </LinkLike>
          </span>
        )}
      </div>
    );
  }
}

export default Terminal;



// WEBPACK FOOTER //
// ./src/app/components/partials/box-office/box-office-terminal.jsx